import { CategoryPicker, getNoCategoryItem } from "app/components/React/CategoriesDropdown/CategoryPicker";
import { PlainTooltip } from "app/components/React/Tooltip/PlainTooltip/PlainTooltip";
import type { FC } from "react";
import styled from "styled-components";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { colorsPalettes } from "@similarweb/styles";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { ICategory } from "app/common/services/categoryService.types";
import type { ICategoryItem } from "../CategoriesDropdown/CategoryDropdown";
import categoryService from "app/common/services/categoryService";

const noCategoryItem = getNoCategoryItem();

const DropdownWrapper = styled.div`
    width: 100%;

    .CategoryDropdownItem--parent {
        color: ${colorsPalettes.carbon[400]};
        font-weight: 400;
    }

    .DropdownButton .CategoryDropdownItem--child {
        padding-left: 0;
    }
`;

interface ICategoryBenchmarkDropdown {
    category: string;
    categories?: ICategoryItem[];
    includeSharedCustomeCategories?: boolean;
    excludeCustomCategoriesWithSegments?: boolean;
    urlParamName?: string;
    onDropdownToggleCallback?: (isOpen: boolean) => void;
    onCategorySelectedCallback?: (category: ICategory) => void;
    isDropdownDisabled?: boolean;
    isTooltipVisible?: boolean;
    placeholder?: ICategoryItem;
    buttonWidth?: string | number;
    text: {
        tooltip: string;
        searchPlaceHolder: string;
    };
}

export const CategoryBenchmarkDropdown: FC<ICategoryBenchmarkDropdown> = ({
    category,
    categories,
    includeSharedCustomeCategories,
    excludeCustomCategoriesWithSegments,
    urlParamName = "category",
    onDropdownToggleCallback = (isOpen) => null,
    onCategorySelectedCallback = (category) => null,
    isDropdownDisabled,
    isTooltipVisible = true,
    text,
    placeholder,
    buttonWidth = "100%",
}) => {
    const categoriesList =
        categories ??
        categoryService.getFlattenedCategoriesList(includeSharedCustomeCategories, excludeCustomCategoriesWithSegments);
    const { tooltip, searchPlaceHolder } = text;
    const selectedCategoryId = categoryService.getCategory(category)?.id ?? noCategoryItem.id;

    const onDropdownToggle = (isOpen: boolean) => {
        onDropdownToggleCallback(isOpen);
    };

    const onCategoryChanged = (id: string) => {
        proNavigator.updateParams({ [urlParamName]: id });
    };

    const onCategorySelected = (category: ICategory) => {
        const { id } = category;
        onCategorySelectedCallback(category);
        onCategoryChanged(id);
    };

    return (
        <FlexRow alignItems={"center"}>
            <PlainTooltip
                text={tooltip}
                cssClass="categorypicker-tooltip"
                enabled={isTooltipVisible}
            >
                <DropdownWrapper data-automation="dropdown-category">
                    <CategoryPicker
                        selectedCategoryId={selectedCategoryId}
                        onToggle={onDropdownToggle}
                        onSelect={onCategorySelected}
                        disabled={isDropdownDisabled}
                        searchPlaceHolder={searchPlaceHolder}
                        dropdownPopupPlacement={"ontop-left"}
                        buttonWidth={buttonWidth}
                        placeHolder={placeholder}
                        categories={categoriesList}
                    />
                </DropdownWrapper>
            </PlainTooltip>
        </FlexRow>
    );
};
